import type { C1_Project } from "../model/C1_Project";
import type { C1_EloItem_Data } from "../types/C1_EloTypes";

export type C1_TagNumbering = { frozen?: boolean; masterIndexHighWater: number; reservedTags: Record<string, string[]> };

/** Persistent reservations include deleted objects, so gaps are never recycled. */
export function frozenTagAllocator(project: C1_Project) {
  const ledger: C1_TagNumbering = structuredClone(project.data.tagNumbering ?? { masterIndexHighWater: 0, reservedTags: {} });
  const reserve = (scope: string, tag: string) => {
    const tags = ledger.reservedTags[scope] ??= [];
    if (tag && !tags.includes(tag)) tags.push(tag);
  };
  const existingEloTag = (item: C1_EloItem_Data) => state.objectRegistry.getObject(item.id)?.tag ?? "";
  const visit = (item: C1_EloItem_Data) => {
    reserve("elo", existingEloTag(item));
    for (const child of item.items ?? []) visit(child);
  };
  for (const floor of project.floors) {
    reserve("floors", floor.tag);
    for (const room of floor.rooms) {
      reserve(`rooms:${floor.id}`, room.tag);
      for (const object of [room, ...room.openings, ...room.furnitures, ...room.interactiveEloItems]) {
        ledger.masterIndexHighWater = Math.max(ledger.masterIndexHighWater, object.masterIndex ?? 0);
      }
      for (const owner of [room, ...room.openings, ...room.furnitures]) if (owner.eloTree) visit(owner.eloTree);
      for (const item of room.interactiveEloItems) visit(item.toLegacyTreeData());
    }
  }
  const suffixNumber = (suffix: string) => [...suffix.toUpperCase()].reduce((number, letter) => number * 26 + letter.charCodeAt(0) - 64, 0);
  const suffixLetters = (number: number) => {
    let result = "";
    while (number > 0) { number--; result = String.fromCharCode(65 + number % 26) + result; number = Math.floor(number / 26); }
    return result;
  };
  return {
    ledger,
    existingEloTag,
    index(object: { masterIndex?: number; setMasterIndex(value: number): void }, room = false) {
      if (object.masterIndex !== undefined) return;
      const next = ledger.masterIndexHighWater + 1;
      ledger.masterIndexHighWater = room ? Math.ceil(Math.max(10, next) / 10) * 10 : next;
      object.setMasterIndex(ledger.masterIndexHighWater);
    },
    structuralTag(scope: string, existing: string, proposed: string) {
      if (existing) { reserve(scope, existing); return existing; }
      const tags = ledger.reservedTags[scope] ?? [];
      if (/^\d+$/.test(proposed)) {
        const numbers = tags.filter(tag => /^\d+$/.test(tag)).map(Number);
        const tag = tags.includes(proposed) ? String(Math.max(...numbers, Number(proposed)) + 1) : proposed;
        reserve(scope, tag);
        return tag;
      }
      const base = proposed.replace(/\d+$/, "");
      const matches = tags.filter(tag => tag.replace(/\d+$/, "") === base);
      const tag = matches.length ? base + (Math.max(...matches.map(tag => Number(tag.slice(base.length)) || 0)) + 1) : proposed;
      reserve(scope, tag);
      return tag;
    },
    eloTag(item: C1_EloItem_Data, proposed: string | undefined) {
      const existing = existingEloTag(item);
      if (existing || !proposed) { reserve("elo", existing); return existing || undefined; }
      const match = proposed.match(/^([A-Z]\d+)([A-Za-z]*)$/);
      let tag = proposed;
      if (match) {
        const [, root, suffix] = match;
        const siblings = (ledger.reservedTags.elo ?? []).filter(candidate => candidate.match(/^([A-Z]\d+)/)?.[1] === root);
        if (siblings.length) {
          const next = Math.max(...siblings.map(candidate => suffixNumber(candidate.slice(root.length)))) + 1;
          const letters = suffixLetters(next);
          const lowercase = suffix ? suffix === suffix.toLowerCase() : siblings.some(candidate => {
            const existingSuffix = candidate.slice(root.length);
            return existingSuffix && existingSuffix === existingSuffix.toLowerCase();
          });
          tag = root + (lowercase ? letters.toLowerCase() : letters);
        }
      }
      reserve("elo", tag);
      return tag;
    },
  };
}
